import {Injectable} from '@angular/core';
import {AngularFireStorage} from '@angular/fire/compat/storage';
import type {PublicProfileStationeryTemplate} from '@settings/models/publicProfile.settings.interface';
import {AuthService} from '@shared/services/auth.service';
import {PdfService} from '@shared/services/pdf.service';
import type {VatLine} from '@vat/engine/types';
import {ExportOrchestratorService} from 'app/shared/services/export-orchestrator.service';
import {defaultIfEmpty, firstValueFrom, lastValueFrom, take} from 'rxjs';
import {sha256Hex} from '../export/sha256';
import {buildVatExcelModel, writeVatExcelWorkbook} from '../export/vat-excel';
import {buildVatExportEntry} from '../export/vat-export-entry';
import {buildKz48FileName, buildObjektFileName, buildVatExportFileName} from '../export/vat-export-file-name';
import {VatExportSnapshot, VatObjektExportFormat, VatReturnExport} from '../export/vat-export.types';
import {periodLabel} from '../export/vat-format';
import {buildVatKz48DocDefinition, Kz48NachweisInput} from '../export/vat-kz48-nachweis';
import type {ObjektAbrechnungInput, ObjektAbrechnungModel} from '../export/vat-objekt-abrechnung';
import {buildObjektExcelModel} from '../export/vat-objekt-excel';
import {buildVatObjektDocDefinition, objektPeriodLabel} from '../export/vat-objekt-pdf';
import {buildVatVordruckDocDefinition} from '../export/vat-pdf-vordruck';
import {VAT_OBJEKT_DIALOG_COPY} from '../vat-copy';
import {VatReturnLinesChunk, VatReturnsService} from './vat-returns.service';

export interface VatExportResult {
    entry: VatReturnExport;
    blob: Blob;
    fileName: string;
}

const PDF_MIME = 'application/pdf';
const VAT_SOURCE_FEATURE = 'VAT';

/**
 * Dünne Angular-Schicht der Exporte (Spec 7): rendert die reinen Builder, lädt PDFs nach
 * `vatReturns/{id}/{fileName}` (Storage, nur application/pdf), liefert Download/Dateimanager über den
 * ExportOrchestrator und baut den exports[]-Eintrag. Schreibt NICHT auf vatReturns – das übernimmt
 * der Plan-3-Workflow (`markExported`) über die Fassade.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
@Injectable({providedIn: 'root'})
export class VatExportService {

    constructor(
        private pdfService: PdfService,
        private storage: AngularFireStorage,
        private exportOrchestrator: ExportOrchestratorService,
        private vatReturnsService: VatReturnsService,
        private authService: AuthService,
    ) {
    }

    /** Snapshot-Zeilen aus den Chunks `lines/{chunk_000…}` (Spec 3.4), nach index geordnet. */
    async loadLines(returnID: string): Promise<VatLine[]> {
        const snapshot = await firstValueFrom(this.vatReturnsService.linesCollection(returnID).get({source: 'server'}));
        return snapshot.docs
            .map(doc => doc.data() as VatReturnLinesChunk)
            .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
            .flatMap(chunk => (chunk.lines ?? []) as VatLine[]);
    }

    async renderPdf(docDef: any): Promise<Blob> {
        const buffer = await this.pdfService.getBuffer(docDef);
        return new Blob([buffer], {type: PDF_MIME});
    }

    storagePath(returnID: string, fileName: string): string {
        return this.authService.buildPath(`vatReturns/${returnID}/${fileName}`);
    }

    /** Muster deposit-settlement-dispatch.uploadPdf: snapshotChanges() schließt mit dem Upload ab. */
    async uploadPdf(returnID: string, fileName: string, blob: Blob): Promise<{ filePath: string; url: string | null }> {
        const filePath = this.storagePath(returnID, fileName);
        const task = this.storage.upload(filePath, blob, {contentType: PDF_MIME});
        await lastValueFrom(task.snapshotChanges().pipe(defaultIfEmpty(null)));
        const url = await firstValueFrom(this.storage.ref(filePath).getDownloadURL().pipe(take(1)));
        return {filePath, url: (url as string) ?? null};
    }

    /** `propertyID` hängt die Datei im Dateimanager an die Immobilie (Objekt-Abrechnung, Spec 2.3 „Ablage“). */
    deliver(blob: Blob, fileName: string, saveToFolder: boolean, ctx: { returnID: string | null; description: string; propertyID?: string | null }): Promise<void> {
        return this.exportOrchestrator
            .processAndSaveFile(blob, fileName, saveToFolder ? 'both' : 'download', {
                showAllFolders: true,
                sourceFeature: VAT_SOURCE_FEATURE,
                sourceFeatureID: ctx.returnID,
                description: ctx.description,
                ...(ctx.propertyID ? {propertyID: ctx.propertyID} : {}),
            })
            .then(() => undefined);
    }

    async exportVordruckPdf(snapshot: VatExportSnapshot, stationery: PublicProfileStationeryTemplate | null, saveToFolder: boolean): Promise<VatExportResult> {
        const result = await this.renderVordruck(snapshot, stationery);
        await this.deliver(result.blob, result.fileName, saveToFolder, {returnID: snapshot.vatReturn.id, description: this.description(snapshot, 'Vordruck-PDF')});
        return result;
    }

    async exportHerleitungExcel(snapshot: VatExportSnapshot, saveToFolder: boolean): Promise<VatExportResult> {
        const result = await this.renderExcel(snapshot);
        await this.deliver(result.blob, result.fileName, saveToFolder, {returnID: snapshot.vatReturn.id, description: this.description(snapshot, 'Herleitung (Excel)')});
        return result;
    }

    /** Kz-48-Jahresnachweis (A4): kein VatReturn, kein Storage, kein exports[] – nur Download/Dateimanager. */
    async exportKz48Pdf(input: Kz48NachweisInput, saveToFolder: boolean): Promise<{ fileName: string; sha256: string; blob: Blob }> {
        const blob = await this.renderPdf(buildVatKz48DocDefinition(input));
        const fileName = buildKz48FileName(input.landlord.name, input.year);
        const sha256 = await sha256Hex(blob);
        await this.deliver(blob, fileName, saveToFolder, {returnID: null, description: `Kz-48-Nachweis ${input.year} – ${input.landlord.name}`});
        return {fileName, sha256, blob};
    }

    /**
     * Umsatzsteuerabrechnung je Objekt (Spec 2.3, Task C2): PDF oder Excel aus dem reinen Modell, kein VatReturn,
     * kein Storage, kein exports[] (Muster Kz 48) – Datei hängt im Dateimanager an der Immobilie.
     */
    async exportObjekt(model: ObjektAbrechnungModel, input: ObjektAbrechnungInput, format: VatObjektExportFormat, saveToFolder: boolean, propertyID: string): Promise<{ fileName: string; sha256: string; blob: Blob }> {
        const blob = format === 'excel'
            ? await writeVatExcelWorkbook(buildObjektExcelModel(model, input))
            : await this.renderPdf(buildVatObjektDocDefinition(model, input));
        const fileName = buildObjektFileName(input.landlord.name, input.property.label, input.period.year, input.period.periodKey, format === 'excel' ? 'xlsx' : 'pdf');
        const sha256 = await sha256Hex(blob);
        await this.deliver(blob, fileName, saveToFolder, {
            returnID: null,
            propertyID,
            description: VAT_OBJEKT_DIALOG_COPY.fileDescription(input.property.label, objektPeriodLabel(input.period), input.landlord.name),
        });
        return {fileName, sha256, blob};
    }

    /** Für den Steuerberater-Mail-Entwurf: beide Dateien als File, PDF im Storage, kein Download. */
    async buildMailAttachments(snapshot: VatExportSnapshot, stationery: PublicProfileStationeryTemplate | null): Promise<{ pdf: VatExportResult; xlsx: VatExportResult; files: File[] }> {
        const pdf = await this.renderVordruck(snapshot, stationery);
        const xlsx = await this.renderExcel(snapshot);
        return {
            pdf,
            xlsx,
            files: [new File([pdf.blob], pdf.fileName, {type: PDF_MIME}), new File([xlsx.blob], xlsx.fileName, {type: xlsx.blob.type})],
        };
    }

    private async renderVordruck(snapshot: VatExportSnapshot, stationery: PublicProfileStationeryTemplate | null): Promise<VatExportResult> {
        const blob = await this.renderPdf(buildVatVordruckDocDefinition(snapshot, {stationery}));
        const fileName = buildVatExportFileName({...this.fileNameParts(snapshot), extension: 'pdf'});
        const sha256 = await sha256Hex(blob);
        const {filePath, url} = await this.uploadPdf(snapshot.vatReturn.id, fileName, blob);
        return {blob, fileName, entry: buildVatExportEntry({type: 'PDF', fileName, filePath, url, sha256, uid: this.uid(), now: new Date()})};
    }

    private async renderExcel(snapshot: VatExportSnapshot): Promise<VatExportResult> {
        const blob = await writeVatExcelWorkbook(buildVatExcelModel(snapshot));
        const fileName = buildVatExportFileName({...this.fileNameParts(snapshot), extension: 'xlsx', kind: 'HERLEITUNG'});
        const sha256 = await sha256Hex(blob);
        return {blob, fileName, entry: buildVatExportEntry({type: 'EXCEL', fileName, filePath: null, url: null, sha256, uid: this.uid(), now: new Date()})};
    }

    private fileNameParts(snapshot: VatExportSnapshot): { landlordName: string; year: number; periodKey: string; version: number } {
        return {landlordName: snapshot.landlord.name, year: snapshot.vatReturn.year, periodKey: snapshot.vatReturn.periodKey, version: snapshot.vatReturn.version};
    }

    private description(snapshot: VatExportSnapshot, kind: string): string {
        return `Umsatzsteuer-Voranmeldung ${periodLabel(snapshot.vatReturn.year, snapshot.vatReturn.periodKey)} – ${snapshot.landlord.name} – ${kind} (v${snapshot.vatReturn.version})`;
    }

    private uid(): string {
        return this.authService.authUser?.uid ?? '';
    }
}
